
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { sp } from "@pnp/sp";
import { iif } from "rxjs";
import { ListItem, Aem, AemComment } from "./models";

export function delay(timeout: number): Promise<void> {
  return new Promise<void>(resolve => setTimeout(resolve, timeout));
}

export function addDays(date: string | Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function toISODate(date: Date): string {
  return date.getUTCFullYear().toString() + '-' + (date.getMonth() + 1).toString().padStart(2, '0')
    + '-' + date.getDate().toString().padStart(2, '0');
}

export function today(): string {
  const now = new Date();
  const dd = now.getDate().toString().padStart(2, '0');
  const mm = (now.getMonth() + 1).toString().padStart(2, '0'); // January is 0!
  const yyyy = now.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}

export function yesterday(): string {
  const yes = new Date();

  yes.setDate(yes.getDate() - 1);

  const dd = yes.getDate().toString().padStart(2, '0');
  const mm = (yes.getMonth() + 1).toString().padStart(2, '0'); // January is 0!
  const yyyy = yes.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}

export function newId(): string {
  const ms = new Date().getTime().toString(16).padStart(12, '0');
  const a = new Uint8Array(10);
  crypto.getRandomValues(a);
  // eslint-disable-next-line no-bitwise
  a[0] = a[0] & 15 | 64;
  // eslint-disable-next-line no-bitwise
  a[2] = a[2] & 63 | 128;
  return ms + Array.prototype.map.call(a, (x: any) => x.toString(16).padStart(2, '0')).join('');
}

const unretriableStatuses = [400, 401, 403, 404];

export async function execute<T>(action: () => Promise<T>): Promise<T> {
  let timeout = 200;
  let attempt = 0;
  const start = new Date().getTime();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await action();
    }
    catch (error) {
      console.warn(error);
      attempt++;
      if ((error instanceof FatalError) || unretriableStatuses.includes(error.status)
        || attempt > 10 || (new Date().getTime() - 10000 > start)) {
        throw error;
      }
      await delay(timeout);
      timeout = timeout * 2;
    }
  }
}

export function getMonday(date?: Date): Date {
  const d = date && new Date(date) || new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0);
  return d;
}

const monthNames = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

const monthAbbreviations = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
];

const weekDayNames = [
  'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'
];

export function getMonthName(date: Date): string {
  return monthNames[date.getMonth()];
}

export function getMonthAbbreviation(date: Date): string {
  return monthAbbreviations[date.getMonth()];
}

export function getWeekDayName(date: Date): string {
  return weekDayNames[date.getDay()];
}

export class FatalError extends Error {

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  constructor(message: string, public readonly innerError?: any) {
    super(message);
    this.name = 'FatalError';
  }
}

export enum SortDirection {
  None = 0,
  Ascending = 1,
  Descending = 2
}

export interface SortItem {
  propertyPath: string;
  properties?: string[];
  direction: SortDirection;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function getValue(obj: any, properties: string[], toLowerCase = true): any {
  let v = obj;
  if (obj === undefined || obj === null || obj === '') return null;
  for (const propertyName of properties) {
    v = v[propertyName];
    if (v === undefined || v === null || v === '') return null;
  }
  if (typeof v === 'string' && toLowerCase) return v.toLowerCase();
  return v;
}

export function compare(v1: string | Date | number, v2: string | Date | number): number {
  if (v1 == v2) return 0;
  if (v1 == null) return -1;
  if (v2 == null) return 1;
  if (v1 < v2) return -1;
  if (v1 > v2) return 1;
  return 0;
}

export function sort(a: any[], propertyPath: string, direction: SortDirection): void {
  const properties = propertyPath.split('.');
  a.sort((obj1, obj2) => {
    const v1 = getValue(obj1, properties);
    const v2 = getValue(obj2, properties);
    const returnValue = compare(v1, v2);
    if (direction === SortDirection.Descending) return -returnValue;
    return returnValue;
  });
}


export function parseItemDates(items: ListItem[]): void {
  items.forEach(it => {
    it.Created = it.Created && new Date(it.Created);
    it.Modified = it.Modified && new Date(it.Modified);
  })
}

export function* iterableMap<TItem, TResult>(
  items: Iterable<TItem>,
  mapFunction: (item: TItem) => TResult): Generator<TResult, void> {
  for (const item of items) {
    yield mapFunction(item);
  }
}

/*
function test(items: ACResourceDetail<any>[]): void {
  const distinctResourceTitles = distinct(iterableMap(items, (item) => item.Title));
  console.log(distinctResourceTitles);
}
*/

export interface CountItem {
  item: string;
  count: number;
}


export function distinctCount(items: Iterable<string>, sortDirection: 'None' | 'Ascending' | 'Descending'): CountItem[] {
  const map = new Map<string, CountItem>();

  for (const distItem of items) {
    const isStr = typeof distItem === 'string';
    if (isStr) {
      const key = distItem ? distItem.toLowerCase() : null;
      let countItem = map.get(key);
      if (!countItem) {
        countItem = { item: distItem, count: 0 };
        map.set(key, countItem);
      }
      countItem.count++;
    }
    else {
      if (Array.isArray(distItem)) {
        const arrayValue = [...distItem];
        arrayValue.forEach(element => {
          const strVal: string = element;
          const key = strVal ? strVal.toLowerCase() : null;
          let countItem = map.get(key);
          if (!countItem) {
            countItem = { item: strVal, count: 0 };
            map.set(key, countItem);
          }
          countItem.count++;
        });

      }
    }
  }
  const keys: string[] = [];

  if (sortDirection === 'None') {
    const result: CountItem[] = [];
    for (const countItem of map.values()) {
      result.push(countItem);
    }
    return result;
  }
  for (const key of map.keys()) {
    keys.push(key);
  }
  keys.sort((key1, key2) => {
    const count1 = map.get(key1).count;
    const count2 = map.get(key2).count;
    if (sortDirection === 'Ascending') return count1 - count2;
    return count2 - count1;
  });

  const result: CountItem[] = [];
  for (const key of keys) {
    result.push(map.get(key));
  }
  return result;
}

export function distinctDates(items: Iterable<Date>): Date[] {
  const timeSet = new Set<number>();
  for (const item of items) {
    if (item) timeSet.add(item.getTime());
    else timeSet.add(null);
  }
  const timeResult: number[] = [];
  timeSet.forEach(t => {
    timeResult.push(t);
  })
  timeResult.sort((t1, t2) => {
    if (t1 === t2) return 0;
    if (t1 === null) return -1;
    if (t2 === null) return 1;
    return t1 - t2;
  });
  return timeResult.map(x => {
    if (x === null) return null;
    return new Date(x);
  });
}

export function distinct(items: Iterable<string | number>): (string | number)[] {
  const dic: any = {};
  let hasEmpty = false;
  for (const item of items) {
    if (typeof item === 'string') {
      if (item) dic[item.toLowerCase()] = item;
      else hasEmpty = true;
    }
    else if (typeof item === 'number') {
      dic[item] = item;
    }
    else hasEmpty = true;
  }
  const lowerCaseResult: string[] = []
  for (const p in dic) {
    const value = dic[p];
    if (typeof value === 'string') lowerCaseResult.push(p);
    else lowerCaseResult.push(value);
  }
  lowerCaseResult.sort((a, b) => {
    if (a === b) return 0;
    if (a > b) return 1;
    return -1;
  });
  const result: (string | number)[] = [];
  if (hasEmpty) result.push(null);
  lowerCaseResult.forEach(p => {
    result.push(dic[p]);
  })
  return result;
}

export function distinctFaq(items: Iterable<string | number>): (string | number)[] {
  const dic: any = {};
  let hasEmpty = false;
  for (const item of items) {
    if (typeof item === 'string') {
      if (item) dic[item.toLowerCase()] = item;
      else hasEmpty = true;
    }
    else if (typeof item === 'number') {
      dic[item] = item;
    }
    else hasEmpty = true;
  }
  const lowerCaseResult: string[] = []
  for (const p in dic) {
    const value = dic[p];
    if (typeof value === 'string') lowerCaseResult.push(p);
    else lowerCaseResult.push(value);
  }
  lowerCaseResult.sort((a, b) => {
    if (a === b) return 0;
    if (a > b) return 1;
    return -1;
  });
  const result: (string | number)[] = [];
  lowerCaseResult.forEach(p => {
    result.push(dic[p]);
  })
  if (hasEmpty) result.push(null);
  return result;
}

export function getFileNameWithExtension(url: string): string {
  const segments = url.split('/');
  return segments[segments.length - 1];
}

export function getFileNameWithOutExtension(fileNameWithExtension: string): string {
  const tokens = fileNameWithExtension.split('.');
  if (tokens.length === 1) {
    return tokens[0];
  }
  tokens.splice(tokens.length - 1, 1);
  return tokens.join(".");
}

export function getFileNameWithOutExtensionFromUrl(url: string): string {
  const segments = url.split('/');
  const tokens = segments[segments.length - 1].split(".");
  if (tokens.length === 1) {
    return tokens[0];
  }
  tokens.splice(tokens.length - 1, 1);
  return tokens.join(".");
}

export function getFileExtension(url: string): string {
  const tokens = url.split('.');
  return tokens[tokens.length - 1];
}

export function applySort(items: any[], sortItems: SortItem[]): void {
  if (sortItems.length === 0) { return; }
  sortItems.forEach(x => {
    if (x.properties === null || x.properties === undefined || x.properties.length === 0)
      x.properties = x.propertyPath.split('.');
  });
  items.sort((item1, item2) => {
    let compareValue = 0;
    for (const sortItem of sortItems) {
      const v1 = getValue(item1, sortItem.properties);
      const v2 = getValue(item2, sortItem.properties);
      compareValue = compare(v1, v2);
      if (compareValue !== 0) {
        if (sortItem.direction === SortDirection.Descending) {
          return -compareValue;
        }
        return compareValue;
      }
    }
    return 0;
  });
}

export function canAddComments(isOwner: boolean, aemRecord: Aem, versionId: number): boolean {
  let result = false;
  if (isOwner) {
    switch (aemRecord.Status) {
      case 'Draft': {
        !isNaN(versionId) ? result = false : result = true;
        break;
      }
      case 'Review': {
        !isNaN(versionId) ? result = false : result = true;
        break;
      }
      case 'Final': {
        result = false;
        break;
      }
    }
  } else {
    // Visitor
    switch (aemRecord.Status) {
      case 'Draft': {
        result = false;
        break;
      }
      case 'Review': {
        !isNaN(versionId) ? result = false : result = true;
        break;
      }
      case 'Final': {
        result = false;
        break;
      }
    }
  }
  return result;
}

export function canAddCitations(isOwner: boolean, aemRecord: Aem, versionId: number): boolean {
  let result = false;
  if (isOwner) {
    !isNaN(versionId) ? result = false : result = true;
  } else {
    // Visitor
    result = false;
  }
  return result;
}

export function canAddDomainCitations(isOwner: boolean, aemRecord: Aem, versionId: number): boolean {
  let result = false;
  if (isOwner && aemRecord.Status != "Review") {
    result = true;
  } else {
    // Visitor
    result = false;
  }
  return result;
}

export function canEditComments(currentUserTitle: string, isOwner: boolean, aemRecord: Aem, versionId: number, comment: AemComment): boolean {
  let result = false;
  if (isOwner) {
    switch (aemRecord.Status) {
      case 'Draft': {
        (comment.VersionNumber == aemRecord.VersionNumber) ? result = true : result = false;
        break;
      }
      case 'Review': {
        ((comment.VersionNumber == aemRecord.VersionNumber) && (comment.VersionCreater == currentUserTitle)) ? result = true : result = false;
        break;
      }
      case 'Final': {
        result = false;
        break;
      }
    }
  } else {
    // Visitor
    switch (aemRecord.Status) {
      case 'Draft': {
        result = false;
        break;
      }
      case 'Review': {
        ((comment.VersionNumber == aemRecord.VersionNumber) && (comment.VersionCreater == currentUserTitle)) ? result = true : result = false;
        break;
      }
      case 'Final': {
        result = false;
        break;
      }
    }
  }
  return result;
}

export function canEditCitations(isOwner: boolean): boolean {
  let result = false;
  if (isOwner) {
    result = true;
  } else {
    // Visitor
    result = false;
  }
  return result;
}

export function canEditDomainCitations(isOwner: boolean, aemRecord: Aem): boolean {
  let result = false;
  if (isOwner && aemRecord.Status != "Review") {
    result = true;
  } else {
    // Visitor
    result = false;
  }
  return result;
}

export function canDeleteComments(currentUserTitle: string, isOwner: boolean, aemRecord: Aem, comment: AemComment): boolean {
  let result = false;
  if (isOwner) {
    switch (aemRecord.Status) {
      case 'Draft': {
        return true;
        
      }
      case 'Review': {
        ((comment.VersionNumber == aemRecord.VersionNumber) && (comment.VersionCreater == currentUserTitle)) ? result = true : result = false;
        break;
      }
      case 'Final': {
        result = true;
        break;
      }
    }
  } else {
    // Visitor
    switch (aemRecord.Status) {
      case 'Draft': {
        result = false;
        break;
      }
      case 'Review': {
        ((comment.VersionNumber == aemRecord.VersionNumber) && (comment.VersionCreater == currentUserTitle)) ? result = true : result = false;
        break;
      }
      case 'Final': {
        result = false;
        break;
      }
    }
  }
  return result;
}

export function canDeleteCitations(isOwner: boolean): boolean {
  let result = false;
  if (isOwner) {
    return true;
  } else {
    // Visitor
    result = false;
  }
  return result;
}

export function canDeleteDomainCitations(isOwner: boolean, aemRecord: Aem): boolean {
  let result = false;
  if (isOwner && aemRecord.Status != "Review") {
    return true;
  } else {
    // Visitor
    result = false;
  }
  return result;
}

export function concat(separator: string, ...items: string[]): string {
  if (!items) return undefined;
  return items.filter(x => x).join(separator);
}

export function getActualTime(interactions, actualPreparation) {
  if (interactions && interactions.length > 0) {
    const sumofInteraction = sumAvailableHours(interactions, "ActualInteractionHours");
    return addAvailableHours(actualPreparation, sumofInteraction);
  }
  return getTimeAsString(actualPreparation);
}

export function sumHours(requestList: any, keyString: string) {
  if (requestList && requestList.length > 0) {
    return sumAvailableHours(requestList, keyString);
  }
  return '00:00'
}

export function hoursRemainingToday(contract, request) {
  let smallestStartDate = ""
  const startList = (contract.sort((a, b) => {
    return new Date(a.StartDate).getTime() -
      new Date(b.StartDate).getTime();
  }));

  if (startList && startList.length > 0) {
    smallestStartDate = startList[0].StartDate
  }

  let largestExpiringDate = ""
  const expiringList = (contract.sort((a, b) => {
    return new Date(b.ExpirationDate).getTime() -
      new Date(a.ExpirationDate).getTime();

  }));

  if (expiringList && expiringList.length > 0) {
    largestExpiringDate = expiringList[0].ExpirationDate
  }

  const allTotalContractHours = sumAvailableHours(contract, "ContractHours");
  const usedContractHours = sumAvailableHours(contract, "UsedContractHours");
  const allPlannedHours = sumAvailableHours(request.filter((i) => new Date(i.ProjectStartDate).getTime() >= new Date(smallestStartDate).getTime() && new Date(i.ProjectStartDate).getTime() <= new Date(largestExpiringDate).getTime()), "TotalPlannedHours");
  // const allActualInterationHours = sumAvailableHours(interaction.filter((i) => new Date(i.InteractionDate).getTime() >= new Date(smallestStartDate).getTime() && new Date(i.InteractionDate).getTime() <= new Date(largestExpiringDate).getTime()), "ActualInteractionHours");
  const addedPlannedActualHours = addAvailableHours(allPlannedHours, usedContractHours);
  const hoursList = {
    "ReminingPlanned": allPlannedHours,
    "actualUsed": usedContractHours,
    "Remainingtoday": calculateAvailableHours(allTotalContractHours, addedPlannedActualHours)
  }

  return hoursList;
}

export function expiredHoursUnused(contracts: any) {
  const allTotalContractHours = sumAvailableHours(contracts, "ContractHours");
  const usedContractHours = sumAvailableHours(contracts, "UsedContractHours");
  return calculateAvailableHours(allTotalContractHours, usedContractHours);
}

export const sumAvailableHours = (contractedList: any, keyString: string): string => {
  let _hoursRemaining = 0
  if (contractedList === undefined) console.log(contractedList);
  else {
    contractedList.map((contract) => {
      const hoursContracted = getValidatedHourMinutes(contract[keyString] ? contract[keyString] : '00:00');
      const ConHrsdurationInMinutes = Number(hoursContracted.hr) * 60 + Number(hoursContracted.min);
      _hoursRemaining += ConHrsdurationInMinutes;

    });
  }

  return convertTimeToString(_hoursRemaining);
}

export function addAvailableHours(contractedHours: string, hoursUsed: string): string {
  const hoursContracted = getValidatedHourMinutes(contractedHours ? contractedHours : '00:00');
  const usedHours = getValidatedHourMinutes(hoursUsed ? hoursUsed : '00:00');

  const ConHrsdurationInMinutes = Number(hoursContracted.hr) * 60 + Number(hoursContracted.min);
  const UHrsdurationInMinutes = Number(usedHours.hr) * 60 + Number(usedHours.min);
  const _hoursRemaining = ConHrsdurationInMinutes + UHrsdurationInMinutes;

  return convertTimeToString(_hoursRemaining);
}

export function calculateAvailableHours(contractedHours: string, hoursUsed: string): string {
  const _hoursRemaining = calculateHoursDiffAsTime(contractedHours, hoursUsed)
  return convertTimeToString(_hoursRemaining);
}

export function calculateHoursDiffAsTime(contractedHours: string, preparationHours: string): number {
  const hoursContracted = getValidatedHourMinutes(contractedHours ? contractedHours : '00:00');
  const prepHours = getValidatedHourMinutes(preparationHours ? preparationHours : '00:00');

  const ConHrsdurationInMinutes = Number(hoursContracted.hr) * 60 + Number(hoursContracted.min);
  const PrepHrsdurationInMinutes = Number(prepHours.hr) * 60 + Number(prepHours.min);
  const _hoursRemaining = ConHrsdurationInMinutes - PrepHrsdurationInMinutes;
  return _hoursRemaining;
}

export function convertTimeToString(reqNum: any): string {
  const timeseparator = ':';
  const totalHr = Math.floor(reqNum / 60);
  const totalmin = reqNum % 60;

  const h = pad(totalHr);
  const m = pad(totalmin);
  return h + timeseparator + m;
}

export function formatDateShort(str: Date): string {
  return new Date(str).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' });
}

export function getTimeAsString(str: string): string {
  const timeseparator = ':';
  const time = getValidatedHourMinutes(str);
  time.hr = time.hr < 0 ? 0 : time.hr;
  //min = min > 59 ? 59 : min;
  if (time.min > 59) {
    do {
      time.min = time.min - 60;
      time.hr = time.hr + 1;
    } while (time.min > 59);
  }

  time.min = time.min < 0 ? 0 : time.min;
  const h = pad(time.hr);
  const m = pad(time.min);
  return h + timeseparator + m;
}

export function getValidatedHourMinutes(timevalue: string): { hr: number, min: number } {
  try {
    const timeseparator = ':';
    if (timevalue !== undefined && timevalue !== null) {
      const time = timevalue.split(timeseparator);
      let hr = Number(time[0]);
      let min = time.length > 1 ? Number(time[1]) : 0;
      hr = hr < 0 ? 0 : hr;
      //min = min > 59 ? 59 : min;
      if (min > 59) {
        do {
          min = min - 60;
          hr = hr + 1;
        } while (min > 59);
      }
      min = min < 0 ? 0 : min;
      return { hr, min };
    }
  } catch (e) {
    console.log(e);
  }
  return { hr: 0, min: 0 };
}

export function pad(n: number): string {
  if (n && n.toString().length > 0) {
    return n.toString().length === 1 ? '0' + n.toString() : n.toString();
  }
  return "00";
}

export function getFlagName(country: string): string {
  if (!country || country === undefined || typeof country !== 'string' || country.length == 0) return '/assets/flags/xx.svg';

  switch (country) {
    case 'EUnetHTA':
      return '/assets/flags/europe.svg';
    case 'SriLanka':
      return '/assets/flags/sri-lanka.svg';
    case 'Macao':
      return '/assets/flags/macau.svg';
    case 'Czechia':
      return '/assets/flags/czech-republic.svg';
    case 'British Virgin Islands':
      return '/assets/flags/virgin-islands-british.svg';
    case 'Curacao':
      return '/assets/flags/curaçao.svg';
    case 'US Virgin Islands':
      return '/assets/flags/virgin-islands-us.svg';
    case 'Vatican':
      return '/assets/flags/holy-see.svg';
    case 'Republic of the Congo':
      return '/assets/flags/congo.svg';
    case 'Northen Mariana Islands':
      return '/assets/flags/northern-mariana.svg';
    case 'Northern Mariana Islands':
      return '/assets/flags/northern-mariana.svg';
    case 'Eswatini':
      return '/assets/flags/swaziland.svg';
    case 'Community wide':
      return '/assets/flags/xx.svg';
    case 'Community Wide':
      return '/assets/flags/xx.svg';
    default:
      return `/assets/flags/${country.toLowerCase().replace(/ /g, "-",)}.svg`;
  }
}

export function getKeywordsString(keywords: string[]): string {
  let s = '';
  if (keywords !== null && keywords.length > 0) {
    keywords.map((item) => {
      s = `${s},${item}`;
    });
  }
  return s.replace(/(^[,\s]+)|([,\s]+$)/g, "");
}

export function compareStartDate(object1, object2): number {
  const obj1 = object1;
  const obj2 = object2;

  if (obj1 < obj2) {
    return -1;
  }
  if (obj1 > obj2) {
    return 1;
  }
  return 0;
}

export function onFormatDate(date: Date): string {
  return !date ? '' : (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
}


const windowsTimeZoneIdByDescriptionWithoutOffset = {
  "International Date Line West": "Dateline Standard Time",
  "Co-ordinated Universal Time-11": "UTC-11",
  "Aleutian Islands": "Aleutian Standard Time",
  "Hawaii": "Hawaiian Standard Time",
  "Marquesas Islands": "Marquesas Standard Time",
  "Alaska": "Alaskan Standard Time",
  "Co-ordinated Universal Time-09": "UTC-09",
  "Baja California": "Pacific Standard Time (Mexico)",
  "Co-ordinated Universal Time-08": "UTC-08",
  "Pacific Time (US & Canada)": "Pacific Standard Time",
  "Arizona": "US Mountain Standard Time",
  "Chihuahua, La Paz, Mazatlan": "Mountain Standard Time (Mexico)",
  "Mountain Time (US & Canada)": "Mountain Standard Time",
  "Yukon": "Yukon Standard Time",
  "Central America": "Central America Standard Time",
  "Central Time (US & Canada)": "Central Standard Time",
  "Easter Island": "Easter Island Standard Time",
  "Guadalajara, Mexico City, Monterrey": "Central Standard Time (Mexico)",
  "Saskatchewan": "Canada Central Standard Time",
  "Bogota, Lima, Quito, Rio Branco": "SA Pacific Standard Time",
  "Chetumal": "Eastern Standard Time (Mexico)",
  "Eastern Time (US & Canada)": "Eastern Standard Time",
  "Haiti": "Haiti Standard Time",
  "Havana": "Cuba Standard Time",
  "Indiana (East)": "US Eastern Standard Time",
  "Turks and Caicos": "Turks And Caicos Standard Time",
  "Asuncion": "Paraguay Standard Time",
  "Atlantic Time (Canada)": "Atlantic Standard Time",
  "Caracas": "Venezuela Standard Time",
  "Cuiaba": "Central Brazilian Standard Time",
  "Georgetown, La Paz, Manaus, San Juan": "SA Western Standard Time",
  "Santiago": "Pacific SA Standard Time",
  "Newfoundland": "Newfoundland Standard Time",
  "Araguaina": "Tocantins Standard Time",
  "Brasilia": "E. South America Standard Time",
  "Cayenne, Fortaleza": "SA Eastern Standard Time",
  "City of Buenos Aires": "Argentina Standard Time",
  "Greenland": "Greenland Standard Time",
  "Montevideo": "Montevideo Standard Time",
  "Punta Arenas": "Magallanes Standard Time",
  "Saint Pierre and Miquelon": "Saint Pierre Standard Time",
  "Salvador": "Bahia Standard Time",
  "Co-ordinated Universal Time-02": "UTC-02",
  "Mid-Atlantic - Old": "Mid-Atlantic Standard Time",
  "Azores": "Azores Standard Time",
  "Cabo Verde Is.": "Cape Verde Standard Time",
  "Coordinated Universal Time": "UTC",
  "Dublin, Edinburgh, Lisbon, London": "GMT Standard Time",
  "Monrovia, Reykjavik": "Greenwich Standard Time",
  "Sao Tome": "Sao Tome Standard Time",
  "Casablanca": "Morocco Standard Time",
  "Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna": "W. Europe Standard Time",
  "Belgrade, Bratislava, Budapest, Ljubljana, Prague": "Central Europe Standard Time",
  "Brussels, Copenhagen, Madrid, Paris": "Romance Standard Time",
  "Sarajevo, Skopje, Warsaw, Zagreb": "Central European Standard Time",
  "West Central Africa": "W. Central Africa Standard Time",
  "Amman": "Jordan Standard Time",
  "Athens, Bucharest": "GTB Standard Time",
  "Beirut": "Middle East Standard Time",
  "Cairo": "Egypt Standard Time",
  "Chisinau": "E. Europe Standard Time",
  "Damascus": "Syria Standard Time",
  "Gaza, Hebron": "West Bank Standard Time",
  "Harare, Pretoria": "South Africa Standard Time",
  "Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius": "FLE Standard Time",
  "Jerusalem": "Israel Standard Time",
  "Juba": "South Sudan Standard Time",
  "Kaliningrad": "Kaliningrad Standard Time",
  "Khartoum": "Sudan Standard Time",
  "Tripoli": "Libya Standard Time",
  "Windhoek": "Namibia Standard Time",
  "Baghdad": "Arabic Standard Time",
  "Istanbul": "Turkey Standard Time",
  "Kuwait, Riyadh": "Arab Standard Time",
  "Minsk": "Belarus Standard Time",
  "Moscow, St Petersburg": "Russian Standard Time",
  "Nairobi": "E. Africa Standard Time",
  "Volgograd": "Volgograd Standard Time",
  "Tehran": "Iran Standard Time",
  "Abu Dhabi, Muscat": "Arabian Standard Time",
  "Astrakhan, Ulyanovsk": "Astrakhan Standard Time",
  "Baku": "Azerbaijan Standard Time",
  "Izhevsk, Samara": "Russia Time Zone 3",
  "Port Louis": "Mauritius Standard Time",
  "Saratov": "Saratov Standard Time",
  "Tbilisi": "Georgian Standard Time",
  "Yerevan": "Caucasus Standard Time",
  "Kabul": "Afghanistan Standard Time",
  "Ashgabat, Tashkent": "West Asia Standard Time",
  "Ekaterinburg": "Ekaterinburg Standard Time",
  "Islamabad, Karachi": "Pakistan Standard Time",
  "Qyzylorda": "Qyzylorda Standard Time",
  "Chennai, Kolkata, Mumbai, New Delhi": "India Standard Time",
  "Sri Jayawardenepura": "Sri Lanka Standard Time",
  "Kathmandu": "Nepal Standard Time",
  "Astana": "Central Asia Standard Time",
  "Dhaka": "Bangladesh Standard Time",
  "Omsk": "Omsk Standard Time",
  "Yangon (Rangoon)": "Myanmar Standard Time",
  "Bangkok, Hanoi, Jakarta": "SE Asia Standard Time",
  "Barnaul, Gorno-Altaysk": "Altai Standard Time",
  "Hovd": "W. Mongolia Standard Time",
  "Krasnoyarsk": "North Asia Standard Time",
  "Novosibirsk": "N. Central Asia Standard Time",
  "Tomsk": "Tomsk Standard Time",
  "Beijing, Chongqing, Hong Kong SAR, Urumqi": "China Standard Time",
  "Irkutsk": "North Asia East Standard Time",
  "Kuala Lumpur, Singapore": "Singapore Standard Time",
  "Perth": "W. Australia Standard Time",
  "Taipei": "Taipei Standard Time",
  "Ulaanbaatar": "Ulaanbaatar Standard Time",
  "Eucla": "Aus Central W. Standard Time",
  "Chita": "Transbaikal Standard Time",
  "Osaka, Sapporo, Tokyo": "Tokyo Standard Time",
  "Pyongyang": "North Korea Standard Time",
  "Seoul": "Korea Standard Time",
  "Yakutsk": "Yakutsk Standard Time",
  "Adelaide": "Cen. Australia Standard Time",
  "Darwin": "AUS Central Standard Time",
  "Brisbane": "E. Australia Standard Time",
  "Canberra, Melbourne, Sydney": "AUS Eastern Standard Time",
  "Guam, Port Moresby": "West Pacific Standard Time",
  "Hobart": "Tasmania Standard Time",
  "Vladivostok": "Vladivostok Standard Time",
  "Lord Howe Island": "Lord Howe Standard Time",
  "Bougainville Island": "Bougainville Standard Time",
  "Chokurdakh": "Russia Time Zone 10",
  "Magadan": "Magadan Standard Time",
  "Norfolk Island": "Norfolk Standard Time",
  "Sakhalin": "Sakhalin Standard Time",
  "Solomon Is., New Caledonia": "Central Pacific Standard Time",
  "Anadyr, Petropavlovsk-Kamchatsky": "Russia Time Zone 11",
  "Auckland, Wellington": "New Zealand Standard Time",
  "Co-ordinated Universal Time+12": "UTC+12",
  "Fiji": "Fiji Standard Time",
  "Petropavlovsk-Kamchatsky - Old": "Kamchatka Standard Time",
  "Chatham Islands": "Chatham Islands Standard Time",
  "Co-ordinated Universal Time+13": "UTC+13",
  "Nuku'alofa": "Tonga Standard Time",
  "Samoa": "Samoa Standard Time",
  "Kiritimati Island": "Line Islands Standard Time"
};

function getWindowsTimeZoneDescriptionWihoutOffsetByIdDictionary(): any {
  const dic = {} as any;
  for (const key of Object.keys(windowsTimeZoneIdByDescriptionWithoutOffset)) {
    dic[windowsTimeZoneIdByDescriptionWithoutOffset[key]] = key;
  }
  return dic;
}

const windowsTimeZoneDescriptionWithoutOffsetById = getWindowsTimeZoneDescriptionWihoutOffsetByIdDictionary();

export function getWindowsTimeZoneDescriptionWithoutOffsetById(timezoneId: string): string {
  return windowsTimeZoneDescriptionWithoutOffsetById[timezoneId];
}

export function getWindowsTimeZoneDescriptionWithoutOffset(timeZoneDescription: string): string {
  const idx = timeZoneDescription.indexOf(')');
  if (idx < 0) return undefined;
  return timeZoneDescription.substring(idx + 2);
}

export function getWindowsTimeZoneIdByDescription(timeZoneDescription: string): string {
  const descriptionWithoutOffset = getWindowsTimeZoneDescriptionWithoutOffset(timeZoneDescription);
  return descriptionWithoutOffset && windowsTimeZoneIdByDescriptionWithoutOffset[descriptionWithoutOffset];
}

const REGEXP_SCROLL_PARENT = /^(visible|hidden)/

export function getScrollParent(el: HTMLElement): HTMLElement {
  return !(el instanceof HTMLElement) || typeof window.getComputedStyle !== 'function'
    ? null
    : el.scrollHeight >= el.clientHeight && !REGEXP_SCROLL_PARENT.test(window.getComputedStyle(el).overflowY || 'visible')
      ? el
      : getScrollParent(el.parentElement) || document.body
}

export const predefinedClinical = {
  Title1: "Key Data Available (KDA)",
  Title2: "CSR Availability",
};

export const predefinedRegulatory = {
  Title1: "Submission Date",
  Title2: "Marketing Authorization Date",
  Title3: "CHMP Positive Opinion Date"
};

export const predefinedHTA = {
  Title1: "HTA Submission",
  Title2: "HTA Decision",
  Title3: "Reimbursement",
  Title4: "Price Publication"
};

export const predefinedGlobal = {
  Title1: "Core Value Dossier (CVD)",
  Title2: "Integrated Access Strategy (IAS)",
  Title3: "Value Narrative",
  Title4: "Objection Handler"
};

export const getFilteredRecord = (data, CategoryName, type) => {
  const temp = [...data];
  if (CategoryName === "Global Deliverables") {
    return temp.filter(
      (f: any) => f.ID == type.Id
    );
  } else {
    return temp.filter(
      (f: any) => f.TypeID == type.Id
    );
  }
};

export const getColorForCategory = (cat) => {
  if (cat == "Clinical") {
    return "green";
  }
  else if (cat == "Regulatory") {
    return "blue";
  }
  else if (cat == "Reimbursement") {
    return "orange";
  }
  else if (cat == "Global Deliverables") {
    return "red";
  }
  return "white"
};

export const getShape = (CategoryName, title) => {
  if (CategoryName == "Clinical" || CategoryName == "Global Deliverables") {
    return "circle";
  }
  else if (CategoryName == "Regulatory") {
    if (title == predefinedRegulatory.Title1)
      return "diamond";
    else if (title == predefinedRegulatory.Title2 || title == predefinedRegulatory.Title3)
      return "star";
    else
      return "circle"
  }
  else if (CategoryName == "Reimbursement") {
    if (title == predefinedHTA.Title1)
      return "diamond";
    else if (title == predefinedHTA.Title2 || title == predefinedHTA.Title3)
      return "star";
    else
      return "circle";
  }
  return '';
};

export const getCls1 = (CategoryName: string, StartOREnd: string) => {
  if (CategoryName == "Clinical" || CategoryName == "Global Deliverables") {
    if (StartOREnd == "StartDate")
      return "Begin";
    else
      return "End";
  }
  else if (CategoryName == "Regulatory" || CategoryName == "Reimbursement") {
    if (StartOREnd == "StartDate")
      return "End";
    else
      return "Begin";
  }
  return '';
}

export const getMilestoneCase = (CategoryName: string, StartOREnd: string) => {
  if (CategoryName == "Clinical") {
    if (StartOREnd == "StartDate")
      return "Available Date";
    else
      return "Available Date";
  }
  else if (CategoryName == "Global Deliverables") {
    if (StartOREnd == "StartDate")
      return "Draft Date";
    else
      return "Final Date";
  }
  else if (CategoryName == "Regulatory" || CategoryName == "Reimbursement") {
    if (StartOREnd == "StartDate")
      return "Base Case";
    else
      return "Best Case";
  }
  return '';
}

export function compareNgbDates(d1: NgbDateStruct, d2: NgbDateStruct) {
  if (d1 == d2) return 0;
  if (d2 == null) return 1;
  if (d1 == null) return -1;
  if (d2 == null) return 1;
  if (d1.year > d2.year) return 1;
  if (d1.year < d2.year) return -1;
  if (d1.month > d2.month) return 1;
  if (d1.month < d1.month) return -1;
  if (d1.day > d2.day) return 1;
  if (d1.day < d2.day) return 1;
  return 0;
}

export function getCurrentPosition(): Promise<GeolocationPosition> {
  const p = new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(pos => {
      resolve(pos);
    }, error => reject(error), {
      enableHighAccuracy: false,
      maximumAge: 24 * 3600000
    });
  });
  return p;
}

export function distinctContractrights(items: Iterable<string | number>): (string | number)[] {
  const dic: any = {};
  let hasEmpty = false;
  for (const item of items) {
    if (typeof item === 'string') {
      if (item) dic[item.toLowerCase()] = item;
      else hasEmpty = true;
    }
    else if (typeof item === 'number') {
      dic[item] = item;
    }
    else hasEmpty = true;
  }
  const lowerCaseResult: string[] = []
  for (const p in dic) {
    const value = dic[p];
    if (typeof value === 'string') lowerCaseResult.push(p);
    else lowerCaseResult.push(value);
  }
  /* lowerCaseResult.sort((a, b) => {
    if (a === b) return 0;
    if (a > b) return 1;
    return -1;
  }); */
  const result: (string | number)[] = [];
  lowerCaseResult.forEach(p => {
    result.push(dic[p]);
  })
  if (hasEmpty) result.push(null);
  return result;
}

export const getEscapeJsonString = (str: string) => {
  return str.replace(/[\b\f\n\r\t\"\\]/g,function(char){
     switch(char){
       case '\b': return '\\b';
       case '\f': return '\\f';
       case '\n': return '\\n';
       case '\r': return '\\r';
       case '\t': return '\\t';
       case '\"': return '\\"';
       case '\\': return '\\\\';
       default: return char;
     }
  });
}
